import { useCallback, useEffect, useState } from "react";
import {
  useNavigate,
  useLocation,
  useSearchParams,
  useParams,
} from "react-router-dom";
import { useAtom } from "jotai";
import { useSnackbar } from "notistack";
import { defaultReturnUrl } from "../billing/upgradeReturn";
import { catalogDbAtom } from "../catalogs/catalog.state";
import { productDbAtom } from "../products/product.state";
import { storeDbAtom } from "../stores/store.state";
import { helpPanelStateAtom } from "../../containers/copilot/copilotAtoms";
import { useDialogs } from "../../contexts/useDialogs";
import { showIntercom } from "../../services/intercom";
import { getFeature, FeatureKey } from "./features";
import { Catalog } from "../catalogs/catalogs";
import { devWarn } from "../../services/util";
import { trackEvent } from "../analytics/analytics";

/**
 * Action Executor Hook
 *
 * Provides a simple executeAction(key) function that handles all context-aware
 * navigation logic. Encapsulates all the complexity of:
 * - Simple path navigation
 * - Catalog-required actions
 * - Product-required actions
 * - Store-required actions
 * - Special actions (support → Intercom)
 *
 * All action metadata is resolved from features.ts (single source of truth).
 *
 * Usage:
 *   const executeAction = useActionExecutor();
 *   executeAction("import_shopify"); // That's it!
 */
export const useActionExecutor = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const { catalogId } = useParams<{ catalogId: string }>();

  const [catalogDb] = useAtom(catalogDbAtom);
  const [productDb] = useAtom(productDbAtom);
  const [storeDb] = useAtom(storeDbAtom);
  const [, setHelpPanelState] = useAtom(helpPanelStateAtom);

  const { productModalApi } = useDialogs();
  const { enqueueSnackbar } = useSnackbar();

  const [currentCatalog, setCurrentCatalog] = useState<Catalog | null>(null);

  // Track current catalog from URL
  useEffect(() => {
    if (catalogId) {
      const matchingCatalog = catalogDb.find(
        (cat) => cat.catalog.id === catalogId
      );
      if (
        matchingCatalog &&
        matchingCatalog.catalog.id !== currentCatalog?.id
      ) {
        setCurrentCatalog(matchingCatalog.catalog);
      }
    } else {
      setCurrentCatalog(null);
    }
  }, [catalogDb, catalogId, currentCatalog?.id]);

  // Execute action by substep key
  const executeAction = useCallback(
    (substepKey: string): void => {
      // Track action click for analytics
      trackEvent({
        category: "ai-copilot",
        action: "action-click",
        label: substepKey,
        feature: true,
      });

      // Primary path: Use features.ts (single source of truth for all metadata)
      // This replaces the legacy guide.steps workflow resolution
      const feature = getFeature(substepKey as FeatureKey);
      if (feature) {
        // 1a. Catalog-required actions (requiresCatalog metadata)
        if (feature.requiresCatalog) {
          const startMode = feature.startMode || substepKey; // Use startMode or fallback to action key

          if (currentCatalog?.id) {
            // Already in catalog designer - set query param
            setSearchParams({ start: startMode });
          } else if (catalogDb?.[0]?.catalog.id) {
            // Navigate to first available catalog
            navigate(
              `/design/catalog/${catalogDb[0].catalog.id}?start=${startMode}`
            );
          } else {
            // No catalog exists - create a new one with the requested mode
            // startMode maps to catalog creation options: grid, list, etc.
            navigate(`/design/catalog/new?start=${startMode}`);
          }
          return;
        }

        // 1b. Products-required actions
        if (feature.requiresProducts) {
          const productId = Array.from(productDb.productMap.keys())[0];
          if (productId) {
            productModalApi.openEditProduct({
              productId,
              startTab: substepKey === "variants" ? "variants" : undefined,
            });
          } else {
            // No products available - user needs to add products first
            devWarn(
              `[ActionExecutor] Products required for action: ${substepKey}`
            );
            trackEvent({
              type: "error",
              category: "ai-copilot",
              action: "prerequisite-missing",
              label: substepKey,
              metadata: {
                missing: "products",
                requiresProducts: true,
              },
            });
            enqueueSnackbar("Please add products first", { variant: "info" });
          }
          return;
        }

        // 1c. Store-required actions
        if (feature.requiresStore) {
          const startTab = feature.startMode || substepKey; // Use startMode for tab switching or fallback to action key

          if (location.pathname.includes("/showrooms/")) {
            // Already in showroom editor - set query param for tab switching
            setSearchParams({ start: startTab });
          } else {
            const storeId = storeDb?.stores[0]?.store.id;
            if (storeId) {
              // Navigate to first available showroom with tab param
              navigate(`/showrooms/${storeId}?start=${startTab}`);
            } else {
              devWarn(
                `[ActionExecutor] Showroom required for action: ${substepKey}`
              );
              trackEvent({
                type: "error",
                category: "ai-copilot",
                action: "prerequisite-missing",
                label: substepKey,
                metadata: {
                  missing: "showroom",
                  requiresStore: true,
                },
              });
              enqueueSnackbar("Please create a showroom first", {
                variant: "info",
              });
            }
          }
          return;
        }

        // 1d. Special actions
        if (substepKey === "support") {
          showIntercom();
          return;
        }

        if (substepKey === "copilot") {
          // Open the AI help panel
          setHelpPanelState("panel");
          return;
        }

        if (substepKey === "concierge" || substepKey === "import_ai") {
          // Copilot → Concierge handoff: go directly to new session draft
          // Signal HelpButton to reset copilot chat (fresh conversation next time)
          // import_ai = AI-emitted "import via AI" key, same handoff (aliased to concierge)
          sessionStorage.setItem('copilot_needs_reset', 'true');
          setHelpPanelState("collapsed");
          navigate("/concierge/new");
          return;
        }

        // 1e. Internal path navigation
        if (feature.path) {
          // Add ?start= parameter if feature has startMode (for tab switching)
          if (feature.startMode) {
            navigate(`${feature.path}?start=${feature.startMode}`);
          } else if (
            (substepKey === "upgrade_plan" || substepKey === "billing") &&
            defaultReturnUrl(location)
          ) {
            // Upgrading from the Copilot: carry a returnUrl so checkout sends
            // the user back to the page they asked from (a Concierge session,
            // the grid, an import). Without it they land on the billing page
            // with no way back and assume their work was lost. See ChangePlan.
            navigate(
              `${feature.path}?returnUrl=${encodeURIComponent(defaultReturnUrl(location))}`,
            );
          } else {
            navigate(feature.path);
          }
          return;
        }

        // 1f. External link
        if (feature.href) {
          window.open(feature.href, "_blank");
          return;
        }
      }

      // 2. Not found - only happens with invalid action mapping
      devWarn(
        `[ActionExecutor] Unknown action: ${substepKey} (not in features)`
      );
      trackEvent({
        type: "error",
        category: "ai-copilot",
        action: "action-not-found",
        label: substepKey,
        metadata: {
          reason: "not in features",
        },
      });
      enqueueSnackbar("Action not available", { variant: "warning" });
    },
    [
      currentCatalog,
      catalogDb,
      productDb,
      storeDb,
      location,
      navigate,
      setSearchParams,
      setHelpPanelState,
      productModalApi,
      enqueueSnackbar,
    ]
  );

  return executeAction;
};
